import { c as _c } from "react/compiler-runtime";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { AfterDateValueConfigKind, BeforeDateValueConfigKind, BetweenDateValueConfigKind, BetweenValueConfigKind, CohortFilterGranularity, InTheLastValueConfigKind } from '@/api/generated/model';
import { IFilterWhenFields } from '@/components/filters/filter-when-forms';
import { FILTERS } from '@/models/common/filters';
import { formatToHuman } from '@/utils/date.util';
import { nullableNumberToString } from '@/utils/numbers.util';
import { ChartDateRange } from '@/domains/chart/components/create-chart/create-chart-settings/chart-date-range';
import { GenericDatePicker } from '@/components/dates/date-picker';
import { DateObject } from 'react-multi-date-picker';
import { CalendarDays } from 'lucide-react';
import { Input } from '@/comps/primitives/input';
import { List, ListItem } from '@/comps/list';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { Button } from '@/comps/primitives/button';
import { cn } from '@/utils/shad';
import { ActivePeriodsFilterState, AvgSessionDurationUnit, AvgSessionLengthFilterState, convertDurationUnitToSeconds, convertSecondsToDurationUnit, formatDurationUnitLabel, InteractionSpecialFilterState, UsersInteractionsFilterState } from '@/comps/FilterPicker/filter-content-v2/interactionSpecialFilterAdapter';
import { getSpecialTimeKindLabel, SPECIAL_TIME_KINDS, SpecialTimeKind } from '@/comps/FilterPicker/filter-content-v2/specialTimeFilterAdapter';
import { FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS } from '@/comps/FilterPicker/filter-content-v2/constants';
import { AVG_SESSION_DURATION_UNIT_OPTIONS } from '@/domains/live-activity/cohorts/components/rule-builder/avg-session-length-duration';
const NESTED_POPOVER_CONTENT_CLASS = cn(FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS, 'border-zinc-800 bg-[#09090B]');
type Props = {
  column: string;
  initialState: InteractionSpecialFilterState;
  onChange: (state: InteractionSpecialFilterState) => void;
  onApply: (state: InteractionSpecialFilterState) => void;
};
const GRANULARITY_OPTIONS: {
  value: CohortFilterGranularity;
  label: string;
}[] = [{
  value: CohortFilterGranularity.day,
  label: 'days'
}, {
  value: CohortFilterGranularity.week,
  label: 'weeks'
}, {
  value: CohortFilterGranularity.month,
  label: 'months'
}];
const PER_OPTIONS: {
  value: CohortFilterGranularity | 'null';
  label: string;
}[] = [{
  value: CohortFilterGranularity.day,
  label: 'per day'
}, {
  value: CohortFilterGranularity.week,
  label: 'per week'
}, {
  value: CohortFilterGranularity.month,
  label: 'per month'
}, {
  value: 'null',
  label: 'Remove frequency'
}];
function cloneInteractionSpecialState(state: InteractionSpecialFilterState): InteractionSpecialFilterState {
  if (state.column === FILTERS.usersInteraction || state.column === FILTERS.avgSessionLength) {
    return {
      ...state,
      whenFields: {
        ...state.whenFields
      }
    };
  }
  return {
    ...state
  };
}
export function FilterContentV2InteractionSpecialValuePopover(t0) {
  const $ = _c(61);
  const {
    column,
    initialState,
    onChange,
    onApply
  } = t0;
  let t1;
  if ($[0] !== initialState) {
    t1 = () => cloneInteractionSpecialState(initialState);
    $[0] = initialState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [state, setState] = useState(t1);
  const [perPopoverOpen, setPerPopoverOpen] = useState(false);
  const onChangeRef = useRef(onChange);
  let t2;
  let t3;
  if ($[2] !== onChange) {
    t2 = () => {
      onChangeRef.current = onChange;
    };
    t3 = [onChange];
    $[2] = onChange;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== state) {
    t4 = () => {
      onChangeRef.current(state);
    };
    t5 = [state];
    $[5] = state;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = patch => {
      setState(current => ({
        ...current,
        ...patch
      }) as InteractionSpecialFilterState);
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const updateState = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event => {
      const parsed = parseInt(event.target.value, 10);
      updateState({
        firstNumber: Number.isNaN(parsed) ? 0 : parsed
      });
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const handleFirstNumberChange = t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = event_0 => {
      const parsed_0 = parseInt(event_0.target.value, 10);
      updateState({
        secondNumber: Number.isNaN(parsed_0) ? 0 : parsed_0
      });
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleSecondNumberChange = t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = whenFields => {
      setState(current_0 => {
        if (current_0.column !== FILTERS.usersInteraction && current_0.column !== FILTERS.avgSessionLength) {
          return current_0;
        }
        return {
          ...current_0,
          whenFields
        };
      });
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleWhenFieldsChange = t9;
  let t10;
  if ($[12] !== state.column) {
    t10 = value => {
      setPerPopoverOpen(false);
      if (state.column !== FILTERS.usersInteraction) {
        return;
      }
      updateState({
        per: value === "null" ? null : value
      });
    };
    $[12] = state.column;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const handleSelectPer = t10;
  const isBetween = state.comparatorKind === BetweenValueConfigKind.between;
  const usersState = state.column === FILTERS.usersInteraction ? state as UsersInteractionsFilterState : null;
  const activePeriodsState = state.column === FILTERS.activePeriods ? state as ActivePeriodsFilterState : null;
  const avgSessionState = state.column === FILTERS.avgSessionLength ? state as AvgSessionLengthFilterState : null;
  const unitLabel = column === FILTERS.userConversationLength ? "turns" : column === FILTERS.userConversationsCount ? "conversations" : column === FILTERS.activePeriods ? "active periods" : column === FILTERS.avgSessionLength ? null : "interactions";
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = event_1 => {
      const parsed_1 = parseInt(event_1.target.value, 10);
      updateState({
        window: Number.isNaN(parsed_1) ? 0 : parsed_1
      } as Partial<ActivePeriodsFilterState>);
    };
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const handleActivePeriodsWindowChange = t11;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = granularity => {
      updateState({
        granularity
      } as Partial<ActivePeriodsFilterState>);
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const handleActivePeriodsGranularityChange = t12;
  let t13;
  if ($[16] !== state) {
    t13 = event_2 => {
      if (state.column !== FILTERS.avgSessionLength) {
        return;
      }
      const parsed_2 = parseInt(event_2.target.value, 10);
      const displayValue = Number.isNaN(parsed_2) ? 0 : parsed_2;
      const avgState = state as AvgSessionLengthFilterState;
      updateState({
        firstNumber: convertDurationUnitToSeconds(displayValue, avgState.durationUnit)
      } as Partial<AvgSessionLengthFilterState>);
    };
    $[16] = state;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const handleAvgDurationDisplayChange = t13;
  let t14;
  if ($[18] !== state) {
    t14 = event_3 => {
      if (state.column !== FILTERS.avgSessionLength) {
        return;
      }
      const parsed_3 = parseInt(event_3.target.value, 10);
      const displayValue_0 = Number.isNaN(parsed_3) ? 0 : parsed_3;
      const avgState_0 = state as AvgSessionLengthFilterState;
      updateState({
        secondNumber: convertDurationUnitToSeconds(displayValue_0, avgState_0.durationUnit)
      } as Partial<AvgSessionLengthFilterState>);
    };
    $[18] = state;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const handleAvgSecondDurationDisplayChange = t14;
  let t15;
  if ($[20] !== state) {
    t15 = durationUnit => {
      if (state.column !== FILTERS.avgSessionLength) {
        return;
      }
      const avgState_1 = state as AvgSessionLengthFilterState;
      const firstDisplay = convertSecondsToDurationUnit(avgState_1.firstNumber, durationUnit);
      const secondDisplay = convertSecondsToDurationUnit(avgState_1.secondNumber, durationUnit);
      updateState({
        durationUnit,
        firstNumber: convertDurationUnitToSeconds(firstDisplay, durationUnit),
        secondNumber: convertDurationUnitToSeconds(secondDisplay, durationUnit)
      } as Partial<AvgSessionLengthFilterState>);
    };
    $[20] = state;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const handleAvgDurationUnitChange = t15;
  let t16;
  if ($[22] !== state) {
    t16 = () => {
      if (state.column !== FILTERS.avgSessionLength) {
        return;
      }
      const avgState_2 = state as AvgSessionLengthFilterState;
      updateState({
        hasWindow: !avgState_2.hasWindow
      } as Partial<AvgSessionLengthFilterState>);
    };
    $[22] = state;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const handleToggleAvgWindow = t16;
  let t17;
  if ($[24] !== avgSessionState || $[25] !== state.firstNumber) {
    t17 = avgSessionState ? nullableNumberToString(convertSecondsToDurationUnit(avgSessionState.firstNumber, avgSessionState.durationUnit)) : nullableNumberToString(state.firstNumber);
    $[24] = avgSessionState;
    $[25] = state.firstNumber;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const t18 = avgSessionState ? handleAvgDurationDisplayChange : handleFirstNumberChange;
  let t19;
  if ($[27] !== t17 || $[28] !== t18) {
    t19 = <Input value={t17} type="number" className="h-8 w-14 [appearance:textfield] border-zinc-800 bg-[#09090B] text-xs text-white [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" onChange={t18} />;
    $[27] = t17;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== avgSessionState || $[31] !== handleAvgSecondDurationDisplayChange || $[32] !== isBetween || $[33] !== state.secondNumber) {
    t20 = isBetween && <><span className="text-xs text-zinc-400">and</span><Input value={avgSessionState ? nullableNumberToString(convertSecondsToDurationUnit(avgSessionState.secondNumber, avgSessionState.durationUnit)) : nullableNumberToString(state.secondNumber)} type="number" className="h-8 w-14 [appearance:textfield] border-zinc-800 bg-[#09090B] text-xs text-white [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" onChange={avgSessionState ? handleAvgSecondDurationDisplayChange : handleSecondNumberChange} /></>;
    $[30] = avgSessionState;
    $[31] = handleAvgSecondDurationDisplayChange;
    $[32] = isBetween;
    $[33] = state.secondNumber;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== avgSessionState || $[36] !== handleAvgDurationUnitChange || $[37] !== unitLabel) {
    t21 = avgSessionState ? <DurationUnitDropdown value={avgSessionState.durationUnit} onChange={handleAvgDurationUnitChange} /> : unitLabel ? <span className="text-xs text-zinc-400">{unitLabel}</span> : null;
    $[35] = avgSessionState;
    $[36] = handleAvgDurationUnitChange;
    $[37] = unitLabel;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t19 || $[40] !== t20 || $[41] !== t21) {
    t22 = <div className="flex flex-wrap items-center gap-2">{t19}{t20}{t21}</div>;
    $[39] = t19;
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== activePeriodsState) {
    t23 = activePeriodsState && <div className="flex flex-wrap items-center gap-2"><span className="text-xs text-zinc-400">of the last</span><Input value={nullableNumberToString(activePeriodsState.window)} type="number" className="h-8 w-14 [appearance:textfield] border-zinc-800 bg-[#09090B] text-xs text-white [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" onChange={handleActivePeriodsWindowChange} /><GranularityDropdown value={activePeriodsState.granularity} onChange={handleActivePeriodsGranularityChange} /></div>;
    $[43] = activePeriodsState;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== handleSelectPer || $[46] !== perPopoverOpen || $[47] !== usersState) {
    t24 = usersState && <><Popover open={perPopoverOpen} onOpenChange={setPerPopoverOpen}><PopoverTrigger asChild={true}><Button type="button" variant="outline" size="sm" className="h-8 justify-start border-zinc-800 bg-[#09090B] text-xs text-white">{usersState.per ? `per ${usersState.per}` : "+ Select frequency"}</Button></PopoverTrigger><PopoverContent className={cn(NESTED_POPOVER_CONTENT_CLASS, "w-40 p-1")} align="start"><List className="min-w-0">{PER_OPTIONS.map(option => <ListItem key={option.value} className={cn("cursor-pointer rounded-[8px] p-2 text-xs text-white hover:bg-neutral-800", (usersState.per ?? "null") === option.value && "bg-neutral-800")} onClick={() => handleSelectPer(option.value)}>{option.label}</ListItem>)}</List></PopoverContent></Popover><InteractionSpecialWhenFields whenFields={usersState.whenFields} onChange={handleWhenFieldsChange} /></>;
    $[45] = handleSelectPer;
    $[46] = perPopoverOpen;
    $[47] = usersState;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== avgSessionState || $[50] !== handleToggleAvgWindow) {
    t25 = avgSessionState && <><Button type="button" variant="outline" size="sm" className="h-8 justify-start border-zinc-800 bg-[#09090B] text-xs text-white" onClick={handleToggleAvgWindow}>{avgSessionState.hasWindow ? "Remove time window" : "+ Add time window"}</Button>{avgSessionState.hasWindow && <InteractionSpecialWhenFields whenFields={avgSessionState.whenFields} onChange={handleWhenFieldsChange} />}</>;
    $[49] = avgSessionState;
    $[50] = handleToggleAvgWindow;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== onApply || $[53] !== state) {
    t26 = <div className="flex justify-end"><Button type="button" className="h-7 gap-1.5 rounded-base bg-primary/80 px-2 text-[12px] font-semibold text-[#101010] hover:bg-primary/70" onClick={() => onApply(state)}>Apply filter</Button></div>;
    $[52] = onApply;
    $[53] = state;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t22 || $[56] !== t23 || $[57] !== t24 || $[58] !== t25 || $[59] !== t26) {
    t27 = <div className="w-auto max-w-96 overflow-hidden rounded-md border border-zinc-800 bg-[#09090B] shadow-md"><div className="flex flex-col gap-3 p-3">{t22}{t23}{t24}{t25}{t26}</div></div>;
    $[55] = t22;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  return t27;
}
function DurationUnitDropdown(t0) {
  const $ = _c(14);
  const {
    value,
    onChange
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== value) {
    t1 = formatDurationUnitLabel(value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedLabel = t1;
  let t2;
  if ($[2] !== selectedLabel) {
    t2 = <PopoverTrigger asChild={true}><Button type="button" variant="outline" size="sm" className="h-8 min-w-22 justify-between border-zinc-800 bg-[#09090B] px-2 text-xs text-white">{selectedLabel}</Button></PopoverTrigger>;
    $[2] = selectedLabel;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(NESTED_POPOVER_CONTENT_CLASS, "w-32 p-1");
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onChange || $[6] !== value) {
    t4 = AVG_SESSION_DURATION_UNIT_OPTIONS.map(option => <ListItem key={option.value} className={cn("cursor-pointer rounded-[8px] p-2 text-xs text-white hover:bg-neutral-800", value === option.value && "bg-neutral-800")} onClick={() => {
      onChange(option.value);
      setOpen(false);
    }}>{option.label}</ListItem>);
    $[5] = onChange;
    $[6] = value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <PopoverContent className={t3} align="start"><List className="min-w-0">{t4}</List></PopoverContent>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== open || $[11] !== t2 || $[12] !== t5) {
    t6 = <Popover open={open} onOpenChange={setOpen}>{t2}{t5}</Popover>;
    $[10] = open;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function InteractionSpecialWhenFields(t0) {
  const $ = _c(43);
  const {
    whenFields,
    onChange
  } = t0;
  const [kindOpen, setKindOpen] = useState(false);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [rangePopoverOpen, setRangePopoverOpen] = useState(false);
  let t1;
  if ($[0] !== onChange || $[1] !== whenFields) {
    t1 = patch => {
      onChange({
        ...whenFields,
        ...patch
      });
    };
    $[0] = onChange;
    $[1] = whenFields;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const updateWhenFields = t1;
  let t2;
  if ($[3] !== onChange || $[4] !== whenFields) {
    t2 = kind => {
      setKindOpen(false);
      onChange({
        ...whenFields,
        kind,
        pickedDate: kind === AfterDateValueConfigKind.after || kind === BeforeDateValueConfigKind.before ? whenFields.pickedDate : null,
        pickedDates: kind === BetweenDateValueConfigKind.between ? whenFields.pickedDates : null,
        filter: null,
        number: kind === InTheLastValueConfigKind.in_the_last ? whenFields.number || 1 : whenFields.number
      });
    };
    $[3] = onChange;
    $[4] = whenFields;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleSelectWhenKind = t2;
  let t3;
  if ($[6] !== updateWhenFields) {
    t3 = event => {
      const parsed = parseInt(event.target.value, 10);
      updateWhenFields({
        number: Number.isNaN(parsed) ? 0 : parsed
      });
    };
    $[6] = updateWhenFields;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleNumberChange = t3;
  const t4 = whenFields.kind as SpecialTimeKind;
  let t5;
  if ($[8] !== t4) {
    t5 = getSpecialTimeKindLabel(t4);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const selectedKindLabel = t5;
  let t6;
  if ($[10] !== selectedKindLabel) {
    t6 = <PopoverTrigger asChild={true}><Button type="button" variant="outline" size="sm" className="h-8 justify-between border-zinc-800 bg-[#09090B] px-2 text-xs text-white">{selectedKindLabel}</Button></PopoverTrigger>;
    $[10] = selectedKindLabel;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn(NESTED_POPOVER_CONTENT_CLASS, "w-40 p-1");
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== handleSelectWhenKind || $[14] !== whenFields.kind) {
    t8 = SPECIAL_TIME_KINDS.map(kind_0 => <ListItem key={kind_0} className={cn("cursor-pointer rounded-[8px] p-2 text-xs text-white hover:bg-neutral-800", whenFields.kind === kind_0 && "bg-neutral-800")} onClick={() => handleSelectWhenKind(kind_0)}>{getSpecialTimeKindLabel(kind_0)}</ListItem>);
    $[13] = handleSelectWhenKind;
    $[14] = whenFields.kind;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <PopoverContent className={t7} align="start"><List className="min-w-0">{t8}</List></PopoverContent>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== kindOpen || $[19] !== t6 || $[20] !== t9) {
    t10 = <Popover open={kindOpen} onOpenChange={setKindOpen}>{t6}{t9}</Popover>;
    $[18] = kindOpen;
    $[19] = t6;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== handleNumberChange || $[23] !== updateWhenFields || $[24] !== whenFields.granularity || $[25] !== whenFields.kind || $[26] !== whenFields.number) {
    t11 = whenFields.kind === InTheLastValueConfigKind.in_the_last && <><Input value={nullableNumberToString(whenFields.number)} type="number" className="h-8 w-14 [appearance:textfield] border-zinc-800 bg-[#09090B] text-xs text-white [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" onChange={handleNumberChange} /><GranularityDropdown value={whenFields.granularity} onChange={granularity => updateWhenFields({
        granularity
      })} /></>;
    $[22] = handleNumberChange;
    $[23] = updateWhenFields;
    $[24] = whenFields.granularity;
    $[25] = whenFields.kind;
    $[26] = whenFields.number;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== datePopoverOpen || $[29] !== updateWhenFields || $[30] !== whenFields.kind || $[31] !== whenFields.pickedDate) {
    t12 = (whenFields.kind === AfterDateValueConfigKind.after || whenFields.kind === BeforeDateValueConfigKind.before) && <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}><PopoverTrigger asChild={true}><Button type="button" variant="outline" size="sm" className="h-8 border-zinc-800 bg-[#09090B] text-xs text-white"><CalendarDays className="mr-1.5 size-4" />{whenFields.pickedDate ? formatToHuman(whenFields.pickedDate.valueOf()) : "Select date"}</Button></PopoverTrigger><PopoverContent className={NESTED_POPOVER_CONTENT_CLASS} align="start" side="bottom" avoidCollisions={false} onOpenAutoFocus={_temp}><GenericDatePicker value={whenFields.pickedDate ? new DateObject(whenFields.pickedDate) : null} onChange={date => {
          updateWhenFields({
            pickedDate: date?.toDate() ?? null,
            filter: null
          });
        }} onClose={() => setDatePopoverOpen(false)} /></PopoverContent></Popover>;
    $[28] = datePopoverOpen;
    $[29] = updateWhenFields;
    $[30] = whenFields.kind;
    $[31] = whenFields.pickedDate;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== rangePopoverOpen || $[34] !== updateWhenFields || $[35] !== whenFields.kind || $[36] !== whenFields.pickedDates) {
    t13 = whenFields.kind === BetweenDateValueConfigKind.between && <Popover open={rangePopoverOpen} onOpenChange={setRangePopoverOpen}><PopoverTrigger asChild={true}><Button type="button" variant="outline" size="sm" className="h-8 border-zinc-800 bg-[#09090B] text-xs text-white"><CalendarDays className="mr-1.5 size-4" />{whenFields.pickedDates ? `${formatToHuman(whenFields.pickedDates[0].valueOf())} - ${formatToHuman(whenFields.pickedDates[1].valueOf())}` : "Select dates"}</Button></PopoverTrigger><PopoverContent className={NESTED_POPOVER_CONTENT_CLASS} align="start" side="bottom" avoidCollisions={false} onOpenAutoFocus={_temp2}><ChartDateRange onChange={dates => {
          if (dates) {
            updateWhenFields({
              pickedDates: [dates[0].toDate(), dates[1].toDate()],
              filter: null
            });
          }
        }} onClose={() => setRangePopoverOpen(false)} /></PopoverContent></Popover>;
    $[33] = rangePopoverOpen;
    $[34] = updateWhenFields;
    $[35] = whenFields.kind;
    $[36] = whenFields.pickedDates;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t13) {
    t14 = <div className="flex flex-col gap-2"><div className="flex flex-wrap items-center gap-2">{t10}{t11}{t12}{t13}</div></div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
}
function _temp2(event_1) {
  return event_1.preventDefault();
}
function _temp(event_0) {
  return event_0.preventDefault();
}
function GranularityDropdown(t0) {
  const $ = _c(14);
  const {
    value,
    onChange
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== value) {
    t1 = GRANULARITY_OPTIONS.find(option => option.value === value)?.label ?? `${value}s`;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedLabel = t1;
  let t2;
  if ($[2] !== selectedLabel) {
    t2 = <PopoverTrigger asChild={true}><Button type="button" variant="outline" size="sm" className="h-8 min-w-22 justify-between border-zinc-800 bg-[#09090B] px-2 text-xs text-white">{selectedLabel}</Button></PopoverTrigger>;
    $[2] = selectedLabel;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(NESTED_POPOVER_CONTENT_CLASS, "w-32 p-1");
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onChange || $[6] !== value) {
    t4 = GRANULARITY_OPTIONS.map(option_0 => <ListItem key={option_0.value} className={cn("cursor-pointer rounded-[8px] p-2 text-xs text-white hover:bg-neutral-800", value === option_0.value && "bg-neutral-800")} onClick={() => {
      onChange(option_0.value);
      setOpen(false);
    }}>{option_0.label}</ListItem>);
    $[5] = onChange;
    $[6] = value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <PopoverContent className={t3} align="start"><List className="min-w-0">{t4}</List></PopoverContent>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== open || $[11] !== t2 || $[12] !== t5) {
    t6 = <Popover open={open} onOpenChange={setOpen}>{t2}{t5}</Popover>;
    $[10] = open;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}